// â”€â”€â”€ Contacts service (Phase 2 reads + Phase 3 writes) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Read + write access to `contacts/` collection. Phase 4 wraps these into a
// proper SDK that's portable to sibling apps. For now, direct Firestore.
//
// Every write logs to userAuditLogs/ via auditService.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import type { Contact, ContactStatus, ContactSource } from '../types';
import { normalizeEmail, isSyntheticEmail } from '../lib/email';
import { normalizePhone, isValidPhone } from '../lib/phone';
import { classifyEmailDomain } from '../lib/domain';
import { writeAuditLog } from './auditService';

const CONTACTS_COL = collection(db, 'contacts');

// â”€â”€â”€ Reads â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface ContactFilters {
  status?: ContactStatus;
  needsReview?: boolean;
  source?: ContactSource;
  hasAppAccount?: boolean;
}

export async function fetchContacts(
  filters: ContactFilters = {},
  pageLimit = 500,
): Promise<Contact[]> {
  const constraints: QueryConstraint[] = [];

  if (filters.status !== undefined) {
    constraints.push(where('status', '==', filters.status));
  }
  if (filters.needsReview !== undefined) {
    constraints.push(where('needsReview', '==', filters.needsReview));
  }
  if (filters.source !== undefined) {
    constraints.push(where('source', '==', filters.source));
  }
  if (filters.hasAppAccount !== undefined) {
    constraints.push(where('hasAppAccount', '==', filters.hasAppAccount));
  }

  if (filters.needsReview === true) {
    constraints.push(orderBy('firstSeenAt', 'desc'));
  } else {
    constraints.push(orderBy('displayName', 'asc'));
  }
  constraints.push(limit(pageLimit));

  const snap = await getDocs(query(CONTACTS_COL, ...constraints));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Contact));
}

export async function fetchContact(contactId: string): Promise<Contact | null> {
  const snap = await getDoc(doc(db, 'contacts', contactId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Contact;
}

/** Find contact by exact email match. Used for dedup before create. */
export async function findContactByEmail(email: string): Promise<Contact | null> {
  const norm = normalizeEmail(email);
  const snap = await getDocs(
    query(CONTACTS_COL, where('email', '==', norm), limit(1)),
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as Contact;
}

/** Find contact by exact phone match (E.164). Used for dedup. */
export async function findContactByPhone(phone: string): Promise<Contact | null> {
  const norm = normalizePhone(phone);
  if (!norm) return null;
  const snap = await getDocs(
    query(CONTACTS_COL, where('phone', '==', norm), limit(1)),
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as Contact;
}

// â”€â”€â”€ Create â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface ContactCreateInput {
  email: string;
  phone?: string;
  displayName: string;
  user_type?: Contact['user_type'];
  employee_id?: string;
  employment_type?: Contact['employment_type'];
  department?: string;
  jobTitle?: string;
  grade?: string;
  unitId?: string;
  unitName?: string;
  directManagerId?: string;
  isLeader?: boolean;
  site?: string;
  startDate?: string;
  nationality?: string;
  emergencyContact?: string;
  bio?: string;
  photoURL?: string;
  delivery_preference?: Contact['delivery_preference'];
  source: ContactSource;
  needsReview?: boolean;
}

export interface ContactCreateResult {
  contactId: string;
  wasCreated: boolean;
  existing?: Contact;
}

/** Create a contact OR return existing (dedup by email, then phone).
 *  Always writes audit log on actual create. */
export async function createContact(
  input: ContactCreateInput,
): Promise<ContactCreateResult> {
  const email = normalizeEmail(input.email);
  const phone = input.phone ? normalizePhone(input.phone) ?? undefined : undefined;

  if (input.phone && !phone) {
    throw new Error(`Invalid phone format: ${input.phone}`);
  }

  // Dedup
  const byEmail = await findContactByEmail(email);
  if (byEmail) return { contactId: byEmail.id, wasCreated: false, existing: byEmail };
  if (phone) {
    const byPhone = await findContactByPhone(phone);
    if (byPhone) return { contactId: byPhone.id, wasCreated: false, existing: byPhone };
  }

  const { isExternal } = classifyEmailDomain(email);
  const performedBy = auth.currentUser?.uid ?? 'unknown';

  const newRef = doc(CONTACTS_COL);
  const payload: Omit<Contact, 'id'> = {
    email,
    phone: phone ?? undefined,
    displayName: input.displayName,
    status: 'active',
    user_type: input.user_type,
    isExternal,
    employee_id: input.employee_id,
    employment_type: input.employment_type,
    department: input.department,
    jobTitle: input.jobTitle,
    grade: input.grade,
    unitId: input.unitId,
    unitName: input.unitName,
    directManagerId: input.directManagerId,
    isLeader: input.isLeader,
    site: input.site,
    startDate: input.startDate,
    nationality: input.nationality,
    emergencyContact: input.emergencyContact,
    bio: input.bio,
    photoURL: input.photoURL,
    delivery_preference: input.delivery_preference,
    hasAppAccount: false,
    source: input.source,
    firstSeenBy: performedBy,
    firstSeenAt: serverTimestamp(),
    needsReview: input.needsReview ?? false,
    manualEdits: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(newRef, stripUndefined(payload));

  await writeAuditLog({
    subjectContactId: newRef.id,
    action: 'contact_created',
    performedBy,
    performedFromApp: 'account-portal',
    after: { email, displayName: input.displayName, source: input.source },
  });

  return { contactId: newRef.id, wasCreated: true };
}

// â”€â”€â”€ Update â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/** Editable fields on a contact via admin UI. Excludes derived fields
 *  (isExternal, hasAppAccount) and provenance fields (source, firstSeenAt). */
export type ContactEditableField =
  | 'displayName'
  | 'email'
  | 'phone'
  | 'user_type'
  | 'employee_id'
  | 'employment_type'
  | 'department'
  | 'jobTitle'
  | 'grade'
  | 'unitId'
  | 'unitName'
  | 'directManagerId'
  | 'isLeader'
  | 'startDate'
  | 'site'
  | 'nationality'
  | 'emergencyContact'
  | 'bio'
  | 'photoURL'
  | 'delivery_preference';

export type ContactUpdates = Partial<Pick<Contact, ContactEditableField>>;

/** Update a contact. Tracks edited field names in `manualEdits[]` so M365
 *  sync (Phase 13) respects admin overrides. Re-derives `isExternal` if
 *  email changes. Always writes audit log. */
export async function updateContact(
  contactId: string,
  updates: ContactUpdates,
): Promise<void> {
  const ref = doc(db, 'contacts', contactId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Contact not found: ${contactId}`);
  const before = snap.data() as Contact;

  const cleaned: Record<string, any> = stripUndefined(updates);

  // Normalize email/phone if present
  if (typeof cleaned.email === 'string') {
    cleaned.email = normalizeEmail(cleaned.email);
    const { isExternal } = classifyEmailDomain(cleaned.email);
    cleaned.isExternal = isExternal;
  }
  if (typeof cleaned.phone === 'string' && cleaned.phone) {
    if (!isValidPhone(cleaned.phone)) throw new Error(`Invalid phone: ${cleaned.phone}`);
    cleaned.phone = normalizePhone(cleaned.phone);
  }

  if (Object.keys(cleaned).length === 0) return;

  // Track manualEdits â€” union of existing and newly-edited field names.
  const existingEdits: string[] = Array.isArray(before.manualEdits) ? before.manualEdits : [];
  const newEdits = Object.keys(cleaned).filter(
    (k) => k !== 'isExternal', // derived, not a manual edit
  );
  const manualEdits = Array.from(new Set([...existingEdits, ...newEdits]));

  await updateDoc(ref, {
    ...cleaned,
    manualEdits,
    updatedAt: serverTimestamp(),
  });

  const performedBy = auth.currentUser?.uid ?? 'unknown';
  await writeAuditLog({
    subjectContactId: contactId,
    action: 'contact_updated',
    performedBy,
    performedFromApp: 'account-portal',
    before: pickKeys(before, Object.keys(cleaned)),
    after: cleaned,
  });
}

// â”€â”€â”€ Soft delete / restore â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export async function deactivateContact(contactId: string, notes?: string): Promise<void> {
  const ref = doc(db, 'contacts', contactId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Contact not found: ${contactId}`);

  await updateDoc(ref, { status: 'inactive', updatedAt: serverTimestamp() });

  const performedBy = auth.currentUser?.uid ?? 'unknown';
  await writeAuditLog({
    subjectContactId: contactId,
    action: 'contact_deactivated',
    performedBy,
    performedFromApp: 'account-portal',
    before: { status: 'active' },
    after: { status: 'inactive' },
    notes,
  });
}

export async function reactivateContact(contactId: string, notes?: string): Promise<void> {
  const ref = doc(db, 'contacts', contactId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Contact not found: ${contactId}`);

  await updateDoc(ref, { status: 'active', updatedAt: serverTimestamp() });

  const performedBy = auth.currentUser?.uid ?? 'unknown';
  await writeAuditLog({
    subjectContactId: contactId,
    action: 'contact_reactivated',
    performedBy,
    performedFromApp: 'account-portal',
    before: { status: 'inactive' },
    after: { status: 'active' },
    notes,
  });
}

// â”€â”€â”€ Merge duplicates â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface MergeResult {
  survivorId: string;
  mergedId: string;
  fieldsCopied: string[];
}

/** Soft-merge: copy non-empty fields from MERGED into SURVIVOR where
 *  survivor's field is empty. Mark merged as inactive with pointer.
 *
 *  Actual cross-collection FK reassignment (cascadeTasks, KRs, reviews)
 *  is deferred to Phase 11+ when consumer apps reference contactId. Until
 *  then, the `mergedIntoContactId` pointer lets apps resolve to the survivor.
 */
export async function mergeContacts(
  survivorId: string,
  mergedId: string,
  notes?: string,
): Promise<MergeResult> {
  if (survivorId === mergedId) throw new Error('Cannot merge contact into itself');

  const [survivorSnap, mergedSnap] = await Promise.all([
    getDoc(doc(db, 'contacts', survivorId)),
    getDoc(doc(db, 'contacts', mergedId)),
  ]);
  if (!survivorSnap.exists()) throw new Error(`Survivor not found: ${survivorId}`);
  if (!mergedSnap.exists()) throw new Error(`Merged not found: ${mergedId}`);

  const survivor = survivorSnap.data() as Contact;
  const merged = mergedSnap.data() as Contact;

  if (survivor.hasAppAccount && merged.hasAppAccount) {
    throw new Error(
      'Both contacts have linked app users. Demote one first before merging.',
    );
  }

  // Copy non-empty merged fields into survivor where survivor is empty
  const COPYABLE: ContactEditableField[] = [
    'phone', 'employee_id', 'employment_type', 'department', 'jobTitle',
    'grade', 'unitId', 'unitName', 'directManagerId', 'startDate', 'site',
    'nationality', 'emergencyContact', 'bio', 'photoURL',
  ];
  const additions: Record<string, any> = {};
  const fieldsCopied: string[] = [];
  for (const f of COPYABLE) {
    if (!isEmpty((survivor as any)[f]) || isEmpty((merged as any)[f])) continue;
    additions[f] = (merged as any)[f];
    fieldsCopied.push(f);
  }

  const performedBy = auth.currentUser?.uid ?? 'unknown';

  await Promise.all([
    Object.keys(additions).length > 0
      ? updateDoc(doc(db, 'contacts', survivorId), {
          ...additions,
          updatedAt: serverTimestamp(),
        })
      : Promise.resolve(),
    updateDoc(doc(db, 'contacts', mergedId), {
      status: 'inactive',
      mergedIntoContactId: survivorId,
      updatedAt: serverTimestamp(),
    }),
  ]);

  await writeAuditLog({
    subjectContactId: mergedId,
    action: 'contact_merged',
    performedBy,
    performedFromApp: 'account-portal',
    before: { status: merged.status },
    after: { status: 'inactive', mergedIntoContactId: survivorId, fieldsCopied },
    notes: notes ?? `Merged into ${survivorId}`,
  });

  return { survivorId, mergedId, fieldsCopied };
}

// â”€â”€â”€ Hard delete (guarded) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/** Hard-delete a contact. ONLY permitted if no `users/{uid}` links to it
 *  (Phase 11+ will extend this guard to cascadeTasks, reviews, KRs).
 *
 *  Throws with a list of blocking references if deletion is unsafe. */
export async function deleteContact(contactId: string): Promise<void> {
  const ref = doc(db, 'contacts', contactId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Contact not found: ${contactId}`);
  const contact = snap.data() as Contact;

  // Guard: any users/ doc linking back?
  if (contact.hasAppAccount && contact.appUserUid) {
    throw new Error(
      `Cannot hard-delete: contact has linked app user (${contact.appUserUid}). ` +
        `Demote the user first, OR use deactivate to soft-delete.`,
    );
  }

  await deleteDoc(ref);

  const performedBy = auth.currentUser?.uid ?? 'unknown';
  await writeAuditLog({
    subjectContactId: contactId,
    action: 'contact_deleted',
    performedBy,
    performedFromApp: 'account-portal',
    before: pickKeys(contact, ['email', 'displayName', 'status', 'source']),
  });
}

// â”€â”€â”€ Promote / Demote (Phase 7) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

import type { AppRoles, InvitePayload } from '../types';
import { inviteUser, disableUserAccount } from './inviteService';

export interface PromoteInput {
  appRoles?: AppRoles;
  isLeader?: boolean;
}

/** Promote a contact â†’ app user (Firebase Auth + users/{uid} via existing
 *  inviteUser Cloud Function). After invite succeeds, links back:
 *    contacts.{id}.appUserUid = uid
 *    contacts.{id}.hasAppAccount = true
 *
 *  Idempotent: if `contact.hasAppAccount` already true, returns existing uid. */
export async function promoteContactToUser(
  contactId: string,
  input: PromoteInput = {},
): Promise<{ uid: string; wasNewlyPromoted: boolean }> {
  const ref = doc(db, 'contacts', contactId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Contact not found: ${contactId}`);
  const c = snap.data() as Contact;

  if (c.hasAppAccount && c.appUserUid) {
    return { uid: c.appUserUid, wasNewlyPromoted: false };
  }

  if (isSyntheticEmail(c.email)) {
    throw new Error(
      'Cannot promote contact with synthetic email â€” needs a real email for sign-in.',
    );
  }

  // Build InvitePayload from contact data
  const payload: InvitePayload = {
    name:             c.displayName,
    email:            c.email,
    jobTitle:         c.jobTitle,
    grade:            c.grade,
    department:       c.department,
    appRoles:         input.appRoles,
    user_type:        c.user_type,
    isLeader:         input.isLeader ?? c.isLeader,
    unitId:           c.unitId,
    unitName:         c.unitName,
    directManagerId:  c.directManagerId,
  };

  // Calls existing Cloud Function (inviteUser HTTP endpoint) â€” creates
  // Firebase Auth account + users/{uid} doc.
  const result = await inviteUser(payload);

  // Link contact â†” user
  await updateDoc(ref, {
    appUserUid: result.uid,
    hasAppAccount: true,
    updatedAt: serverTimestamp(),
  });

  // Also write contactId onto the new users/{uid} so future shim reads work.
  await updateDoc(doc(db, 'users', result.uid), {
    contactId,
    updatedAt: serverTimestamp(),
  });

  const performedBy = auth.currentUser?.uid ?? 'unknown';
  await writeAuditLog({
    subjectContactId: contactId,
    subjectUid: result.uid,
    action: 'promoted_to_user',
    performedBy,
    performedFromApp: 'account-portal',
    after: { uid: result.uid, appRoles: input.appRoles, isLeader: input.isLeader },
  });

  return { uid: result.uid, wasNewlyPromoted: true };
}

/** Demote: disable Firebase Auth + flip contact.hasAppAccount false.
 *  Reversible by re-running promoteContactToUser (or admin un-disable). */
export async function demoteContactUser(contactId: string, notes?: string): Promise<void> {
  const ref = doc(db, 'contacts', contactId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Contact not found: ${contactId}`);
  const c = snap.data() as Contact;
  if (!c.hasAppAccount || !c.appUserUid) {
    throw new Error('Contact has no linked app user to demote.');
  }

  const uid = c.appUserUid;

  // Disable Firebase Auth account
  await disableUserAccount(uid, true);

  // Update users.{uid}.isActive=false to mirror
  await updateDoc(doc(db, 'users', uid), {
    isActive: false,
    updatedAt: serverTimestamp(),
  });

  // Flip contact pointer
  await updateDoc(ref, {
    hasAppAccount: false,
    updatedAt: serverTimestamp(),
  });

  const performedBy = auth.currentUser?.uid ?? 'unknown';
  await writeAuditLog({
    subjectContactId: contactId,
    subjectUid: uid,
    action: 'demoted_from_user',
    performedBy,
    performedFromApp: 'account-portal',
    notes,
  });
}

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function stripUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;
}

function pickKeys<T extends Record<string, any>>(obj: T, keys: string[]): Partial<T> {
  const out: Partial<T> = {};
  for (const k of keys) if (k in obj) (out as any)[k] = obj[k];
  return out;
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

// Re-export so UI imports can pull these from a single module
export { isSyntheticEmail };
